import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image, TouchableOpacity} from 'react-native';
import {
  Text, TextInput, Button, ActivityIndicator, Snackbar,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import GradientHeader from '../../components/common/GradientHeader';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {useConfigStore} from '../../store/useConfigStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';

const BodyMeasurementScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId, customerGender} = route.params;

  const {
    measurementTypes, bodyProfile, isLoading,
    fetchMeasurementTypes, fetchBodyProfile, saveBodyProfile,
  } = useMeasurementStore();
  const {fetchByType, getProperty} = useConfigStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();

  const [values, setValues] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const effectiveOrgUnitId = orgUnits.length === 1 ? orgUnits[0].id : selectedOrgUnitId;

  useEffect(() => {
    if (orgUnits.length === 0) fetchOrgUnits();
  }, [orgUnits.length, fetchOrgUnits]);

  useEffect(() => {
    if (effectiveOrgUnitId) fetchByType('ORDER', effectiveOrgUnitId);
  }, [effectiveOrgUnitId, fetchByType]);

  const measurementUnit = getProperty('ORDER', 'MEASUREMENT_UNIT', effectiveOrgUnitId) || 'INCHES';
  const unitLabel = measurementUnit === 'CMS' ? 'cms' : 'in';

  useEffect(() => {
    const genderFilter = customerGender === 'MALE' || customerGender === 'FEMALE'
      ? customerGender : null;
    fetchMeasurementTypes(genderFilter);
    fetchBodyProfile(customerId);
  }, [customerId, customerGender, fetchMeasurementTypes, fetchBodyProfile]);

  useEffect(() => {
    if (bodyProfile?.values) {
      const prefilled = {};
      bodyProfile.values.forEach(v => {
        prefilled[v.globalOptionId] = v.value;
      });
      setValues(prefilled);
      if (bodyProfile.notes) {
        setNotes(bodyProfile.notes);
      }
    }
  }, [bodyProfile]);

  const sortedTypes = [...measurementTypes].sort((a, b) =>
    a.gender === 'BOTH' && b.gender !== 'BOTH' ? -1 : a.gender !== 'BOTH' && b.gender === 'BOTH' ? 1 : 0,
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const measurementValues = Object.entries(values)
        .filter(([_, v]) => v && v.toString().trim() !== '')
        .map(([typeId, value]) => ({
          globalOptionId: Number(typeId),
          value: value.toString().trim(),
        }));

      await saveBodyProfile(customerId, {
        measuredAt: new Date().toISOString().split('T')[0],
        notes: notes.trim() || null,
        values: measurementValues,
      });
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [values, notes, customerId, saveBodyProfile, navigation, t]);

  if (isLoading && measurementTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title={t('measurement.bodyMeasurements')} onBack={() => navigation.goBack()} />

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        <View style={styles.unitBanner}>
          <Icon name="ruler" size={16} color={colors.primary} />
          <Text style={styles.unitBannerText}>
            All measurements in {measurementUnit === 'CMS' ? 'Centimeters (cms)' : 'Inches (in)'}
          </Text>
        </View>

        {sortedTypes.map((mt, idx) => {
          const imgUrl = customerGender === 'MALE' ? mt.imageUrl1 : mt.imageUrl2;
          return (
            <View key={mt.id} style={styles.measurementCard}>
              <View style={styles.cardTop}>
                <View style={styles.serialBadge}>
                  <Text style={styles.serialText}>{idx + 1}</Text>
                </View>
                <Text style={styles.measurementName}>{mt.name}</Text>
              </View>
              <View style={styles.cardBottom}>
                {imgUrl ? (
                  <Image source={{uri: imgUrl}} style={styles.measurementImage} />
                ) : (
                  <View style={styles.measurementImageEmpty}>
                    <Icon name="human-male-height-variant" size={28} color={colors.textLight} />
                  </View>
                )}
                <TextInput
                  label={`${mt.name} (${unitLabel})`}
                  value={values[mt.id]?.toString() || ''}
                  onChangeText={v => setValues(prev => ({...prev, [mt.id]: v}))}
                  mode="outlined"
                  style={styles.measurementInput}
                  keyboardType="numeric"
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
              </View>
            </View>
          );
        })}

        {/* Notes */}
        <View style={styles.notesSection}>
          <View style={styles.notesTitleRow}>
            <Icon name="note-text-outline" size={18} color={colors.primary} />
            <Text style={styles.notesTitle}>{t('measurement.notes')}</Text>
          </View>
          <TextInput
            value={notes}
            onChangeText={setNotes}
            mode="outlined"
            style={styles.notesInput}
            multiline
            numberOfLines={3}
            placeholder="Add notes about this measurement..."
            outlineColor={colors.border}
            activeOutlineColor={colors.primary}
          />
        </View>

        <View style={{height: spacing.xxl}} />
      </ScrollView>

      {/* Sticky save button */}
      <View style={styles.saveBar}>
        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}
          buttonColor={colors.primary}>
          {t('common.save')}
        </Button>
      </View>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  content: {flex: 1, padding: spacing.md},

  // Unit banner
  unitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary + '12',
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
  },
  unitBannerText: {fontSize: 13, fontWeight: '600', color: colors.primary},

  // Measurement card
  measurementCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: spacing.md,
    marginBottom: spacing.sm,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  serialBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  serialText: {fontSize: 12, fontWeight: '700', color: colors.primary},
  measurementName: {fontSize: 14, fontWeight: '600', color: colors.text, flex: 1},
  cardBottom: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  measurementImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: colors.background,
  },
  measurementImageEmpty: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: colors.background,
    justifyContent: 'center',
    alignItems: 'center',
  },
  measurementInput: {
    flex: 1,
    backgroundColor: colors.surface,
  },

  // Notes
  notesSection: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: spacing.md,
    marginTop: spacing.sm,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
  },
  notesTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  notesTitle: {fontSize: 14, fontWeight: '600', color: colors.primary},
  notesInput: {backgroundColor: colors.surface},

  // Sticky save
  saveBar: {
    backgroundColor: colors.surface,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.divider,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: -2},
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 4,
  },
  saveButton: {borderRadius: 8},
});

export default BodyMeasurementScreen;
